import { useCallback, useState } from "react";
import { SortColumn } from "react-data-grid";

import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { CursorChangeProps, ResultType } from "../../models";
import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../service/query";
import { ResultData } from "../../service/Shared";
import { flattenObject } from "../../utils";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
} from "../../widget";
import { AvatarCellFormatter } from "../../widget/data/DataGrid/components/CellFormatters";

interface UseWorkflowTaskPanelReturnData {
  handleCloseWorkflowTaskPanel: () => void;
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useOverdueTaskPanel = (
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void,
  setShowWorkflowTaskPanel: (show: boolean) => void
): UseWorkflowTaskPanelReturnData => {
  const [dataIsLoading, setDataIsLoading] = useState(true);

  const handleCloseWorkflowTaskPanel = (): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  };

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      name: "Task",
      key: "nameLink",
      dataType: "string",
      formatter: "buttonLink",
      minWidth: 300,
      filterable: true,
      sortable: true,
    },
    {
      name: "Project",
      key: "projectName",
      dataType: "string",
      formatter: "stringWithEllipsis",
      alignment: "left",
      filterable: true,
      minWidth: 70,
      sortable: false,
    },
    {
      name: "Assigned to",
      key: "avatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: true,
      minWidth: 70,
      sortable: false,
    },
    {
      name: "Status",
      key: "status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      alignment: "center",
      filterable: true,
      minWidth: 140,
      sortable: false,
    },
    {
      name: "Deadline",
      key: "deadlineDate",
      dataType: "string",
      formatter: "dateOnlyWithIndicator",
      alignment: "left",
      filterable: false,
      minWidth: 70,
      sortable: false,
    },
  ];

  const formatData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };
        result.avatar = { fullName: d.assignee?.name };

        result.nameLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;
        result.projectName = d.workflowObjectType === "Project" ? d.workflowObjectName : "";
        return result;
      }) || []
    );
  }, []);

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    filterCriteria.deadlineDate = {
      operator: "lt",
      value: new Date().toISOString().slice(0, 10), // format yyyy-mm-dd
    };

    if (filterCriteria.nameLink) {
      filterCriteria.name = filterCriteria.nameLink;
      filterCriteria.nameLink = undefined;
      delete filterCriteria.nameLink;
    }

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    const sortKeyMap: Record<string, string> = {
      nameLink: "name",
      avatar: "assignee.name",
    };

    await searchWorkflowTasks({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  return {
    handleCloseWorkflowTaskPanel,
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
  };
};
